/* eslint-disable no-underscore-dangle */
import { ImageTile, Map, Overlay } from 'ol';
import { Group, Layer } from 'ol/layer';
import {
  transform, transformExtent,
  get, Projection,
} from 'ol/proj';
import ARENALayerConfig from 'configs/connectedLayersConfig/ARENA';
import { RootState } from 'services/reduxStore';
import ImageLayer from 'ol/layer/Image';
import { BaseLayerConfig } from 'types/app.types';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import { Dispatch, SetStateAction } from 'react';
import ImageWrapper from 'ol/Image';
import { GEOSERVER_API, BASE_LAYER_PROVIDER } from 'configs/endpoints';
import { getImage } from 'services/geoserver/outboundsRequest';
import { isRequestCancelled } from 'services/requestQueue';
import { ImageSourceEvent } from 'ol/source/Image';
import { Coordinate } from 'ol/coordinate';
import { ImageStatic, ImageWMS, XYZ } from 'ol/source';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import KML from 'ol/format/KML';
import { Style } from 'ol/style';
import { floor } from 'ol/math';
import { BoundingBox } from 'utils/types/WMSCapabilities.interface';
import { getForViewAndSize } from 'ol/extent';
import { createLogger } from 'services/debugLogger';
import { createBlobUrl, setUncachedBlobSrc } from 'utils/blobUrl';
import {
  cacheForSrc, getCachedImage, purgeCachedImages, storeCachedImage,
} from 'utils/imageCaches';
import LayerName from './enums';
import { availableProjections, destinationProjection } from '../../../configs/projections';
import { connectionTimeout, imageDimensions } from '../../../configs/initialValues';

const logger = createLogger('MapViewUtils');

interface Params {
  [x: string]: any
}
interface RecreateSourceParams {
    layer: Layer,
    params?: Record<keyof Params, string | undefined>
    currentProjection: string,
    name?: string,
}
interface ErrorEventHandler {
  (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error): any
}

// eslint-disable-next-line no-underscore-dangle
const staticLayersServiceApi = (window as any)._env_.REACT_APP_STATIC_LAYERS_SERVICE_API;

export const buildParamsFromLayerWithTime = (source: ImageStatic): {
  layers: string,
  styles: string,
  extent: BoundingBox[],
  projection: string,
  time: string,
  elevations: number,
  changed: string,
} => ({
  layers: source.get('layers'),
  styles: source.get('styles'),
  extent: source.get('extent'),
  projection: source.get('projection'),
  time: source.get('time'),
  elevations: source.get('elevations'),
  changed: source.get('changed'),
});

export const purgeCache = purgeCachedImages;

export const createTileSource = ({
  layers, styles, projection, isRealTime = false, isPreload = false,
} : {
    layers: string,
    styles?: string,
    projection: string,
    isRealTime?: boolean,
    isPreload?: boolean}) => new TileWMS({
  url: `${GEOSERVER_API}/wms`,
  params: {
    LAYERS: layers,
    TILED: true,
    STYLES: styles,
    // FORMAT: 'image/vnd.jpeg-png',
  },
  interpolate: false,
  serverType: 'geoserver',
  projection,

  tileLoadFunction: async (tile, src) => {
    if (!(tile instanceof ImageTile)) {
      throw new Error('imageTile must be of type ImageTile');
    }
    const cachedImage = getCachedImage(src);
    if (cachedImage) {
      // eslint-disable-next-line no-param-reassign
      const image = tile.getImage() as HTMLImageElement;
      image.src = cachedImage.url;
    } else {
      try {
        const response = await getImage(src, { isRealTime, isPreload });
        const imageData = await response.blob();
        const imageElement = tile.getImage() as HTMLImageElement;
        if (src.includes('base')) {
          // Not cached: nothing will reuse the blob URL once the image has loaded.
          setUncachedBlobSrc(imageElement, imageData);
        } else {
          const url = createBlobUrl(imageData);
          imageElement.src = url;
          storeCachedImage(cacheForSrc(src), src, { url, size: imageData.size });
        }
      } catch (e) {
        // A request dropped from the queue (real-time takes priority) is not an error.
        if (isRequestCancelled(e)) return;
        logger.error('Error when downloading layer', e);
      }
    }
  },
});

export const createWMSImageSource = ({
  layers, projection, onError, isRealTime = false, isPreload = false,
} : {
  layers: string,
  projection: string,
  onError?: ErrorEventHandler,
  isRealTime?: boolean,
  isPreload?: boolean,
}) => new ImageWMS({
  url: `${GEOSERVER_API}/wms`,
  params: {
    LAYERS: layers,
    TILED: true,
  },
  interpolate: false,
  serverType: 'geoserver',
  projection,

  imageLoadFunction: async (tile, src) => {
    try {
      if (!(tile instanceof ImageWrapper)) {
        throw new Error('imageTile must be of type ImageTile');
      }
      const cachedImage = getCachedImage(src);
      if (cachedImage) {
        const image = tile.getImage() as HTMLImageElement;
        image.src = cachedImage.url;
      } else {
        const response = await getImage(src, { isRealTime, isPreload });
        const imageData = await response.blob();
        const image = tile.getImage() as HTMLImageElement;
        if (src.includes('base')) {
          setUncachedBlobSrc(image, imageData);
        } else {
          const url = createBlobUrl(imageData);
          image.src = url;
          storeCachedImage(cacheForSrc(src), src, { url, size: imageData.size });
        }
        if (onError) { image.onerror = onError; }
      }
    } catch (e) {
      // A request dropped from the queue (real-time takes priority) is not an error.
      if (isRequestCancelled(e)) return;
      logger.error('WMS image load error', e);
    }
  },

});

export const getTimes = ({ zoomLevel = 7, steps, layersNumber = 1 }: {
  zoomLevel?: number,
  steps: number,
  layersNumber: number}) => {
  let threshold = layersNumber;
  if (zoomLevel <= 7) {
    threshold *= 400;
  } else {
    threshold *= 300;
  }

  return {
    intervalTime: threshold,
    timeoutTime: steps * threshold,
  };
};

export const getTime = async (warning: string | null, src: string) => {
  const regexp = /\d{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z/gm;

  let time;
  const date = decodeURIComponent(src).match(regexp);

  if (warning) {
    const fallbackDate = warning.match(regexp);

    if (fallbackDate) {
      // eslint-disable-next-line prefer-destructuring
      time = fallbackDate[0];
    } else if (warning.includes('No nearest value found')) {
      time = 'outOfRange';
    }
  } else if (date) {
    // eslint-disable-next-line prefer-destructuring
    time = date[0];
  }
  return time;
};

export const createImageSource = ({
  layers, styles, extent, projection, time: time_,
  elevations, onError, callback, changed, currentProjection,
} : {
    layers: string,
    styles?: string,
    extent?: BoundingBox[],
    elevations?: number,
    projection: string,
    time?: string,
    changed?: string,
    onError?: ErrorEventHandler,
    callback?: (image:HTMLImageElement) => void,
    currentProjection: string,
  }) => {
  let currentExtent: number[] = [0, 0, 0, 0];
  if (extent && extent.length && Array.isArray(extent)) {
    const completeExtent = extent.find((e: BoundingBox) => e.crs === projection);
    if (completeExtent) { currentExtent = completeExtent.extent; }
  }

  if (!availableProjections.some(
    (pro) => pro.epsg === projection,
  )) {
    logger.error('Source Projection not found in the available projections list', layers);
    throw new Error('Source Projection not found in the available projections list');
  }
  const staticSource = new ImageStatic({
    url: `${GEOSERVER_API}/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fvnd.jpeg-png&TRANSPARENT=true&LAYERS=${layers}&TILED=false&STYLES=${styles}${elevations ? `&ELEVATION=${elevations}` : ''}&TIME=${time_}&CRS=${encodeURI(projection)}&WIDTH=${imageDimensions[0]}&HEIGHT=${imageDimensions[1]}&BBOX=${currentExtent.join('%2C')}`,
    interpolate: false,
    imageExtent: transformExtent(currentExtent, projection, availableProjections.find(
      (projection) => projection.name === currentProjection,
    )?.epsg as string),
    imageLoadFunction: async (tile, src) => {
      if (!(tile instanceof ImageWrapper)) {
        throw new Error('imageTile must be of type ImageTile');
      }
      if (!src) {
        throw new Error('src must exists');
      }
      const cachedImage = getCachedImage(src);
      if (cachedImage && cachedImage.time) {
        const event = new ImageSourceEvent('imageloadend', tile) as ImageSourceEvent & {time: string};
        const image = tile.getImage() as HTMLImageElement;
        image.src = cachedImage.url;
        event.time = cachedImage.time;
        staticSource.dispatchEvent(event);
      } else {
        try {
          const event = new ImageSourceEvent('imageloadend', tile) as ImageSourceEvent & {time: string};
          const response = await getImage(src);
          const warning = response.headers.get('warning');
          const imageData = await response.blob();
          const time = !src.includes('base') ? await getTime(warning, src) : undefined;
          const image = tile.getImage() as HTMLImageElement;
          if (!time) {
            // Base layers and images without a resolved time can never be read back from cache.
            setUncachedBlobSrc(image, imageData);
          } else {
            const url = createBlobUrl(imageData);
            image.src = url;
            storeCachedImage(cacheForSrc(src), src, { url, size: imageData.size, time });
          }
          if (onError) { image.onerror = onError; }
          if (time) {
            image.setAttribute('time', time);

            event.time = time;
          }
          staticSource.dispatchEvent(event);
        } catch (e) {
          // A request dropped from the queue (real-time takes priority) is not an error.
          if (isRequestCancelled(e)) return;
          logger.error('Static image source load error', e);
        }
      }
    },
  });
  staticSource.set('layers', layers);
  staticSource.set('styles', styles);
  staticSource.set('extent', extent);
  staticSource.set('projection', projection);
  staticSource.set('time', time_);
  staticSource.set('elevations', elevations);
  staticSource.set('changes', changed);
  staticSource.set('currentExtent', currentExtent);
  return staticSource;
};

export const cloneGroup = (group : Group, currentProjection: string) => {
  const allLayers = group?.getLayersArray() as ImageLayer<ImageStatic>[];
  return new Group({
    layers: allLayers.map((layer) => {
      const source = layer.getSource() as ImageStatic;
      const extent = layer.getExtent();
      const params = buildParamsFromLayerWithTime(source);
      const newLayer = new ImageLayer({
        source: createImageSource({ ...params, currentProjection }),
        extent,
      });
      newLayer.set('name', layer.get('name'));
      newLayer.set('layerIndex', layer.get('layerIndex'));
      newLayer.set('type', LayerName.PrealoadingGeoLayer);
      return newLayer;
    }),
    visible: true,
    opacity: 0,
  });
};

export const cloneLayer = (layer: ImageLayer<ImageStatic>, currentProjection: string) => {
  const source = layer.getSource() as ImageStatic;
  const extent = layer.getExtent();
  const params = buildParamsFromLayerWithTime(source);
  const newLayer = new ImageLayer({
    source: createImageSource({ ...params, currentProjection }),
    extent,
  });
  newLayer.set('name', layer.get('name'));
  newLayer.set('layerIndex', layer.get('layerIndex'));
  newLayer.set('type', LayerName.PrealoadingGeoLayer);
  newLayer.setVisible(true);
  newLayer.setOpacity(0);
  return newLayer;
};

export const getFeatureInfoUrl = ({ resolution, coordinate, params } : {
  resolution: number,
  coordinate: Coordinate | number[],
  params: Record<string, string>,
}) => {
  const extent = getForViewAndSize(
    coordinate,
    resolution,
    0,
    [101, 101],
  );
  const x = floor((coordinate[0] - extent[0]) / resolution, 4);
  const y = floor((extent[3] - coordinate[1]) / resolution, 4);

  const url = `${GEOSERVER_API}/wms?`
        + 'SERVICE=WMS'
        + '&VERSION=1.3.0'
        + '&REQUEST=GetFeatureInfo'
        + `&LAYERS=${params.layers
        }&QUERY_LAYERS=${params.layers
        }&STYLES=${params.styles
        }&TIME=${params.time
        }&BBOX=${extent.join(',')
        }&FEATURE_COUNT=45000`
        + `&HEIGHT=${101
        }&WIDTH=${101
        }&FORMAT=image/png`
        + '&INFO_FORMAT=application/json'
        + `&CRS=${params.projection
        }&I=${x
        }&J=${y
        }&EXCEPTIONS=application/vnd.ogc.se_inimage
        ${params.elevations ? `&ELEVATION:${params.elevations}` : ''}`;

  return url;
};

interface GeneratePopupInfoOptions {
    layers : Layer[],
    coordinate: Coordinate | number[],
    pixel: number[],
    popup: Overlay,
    destinationProjection: string,
    map: Map,
    getLayerTooltip: (
      layer: Layer,
      coordinate: Coordinate | number[],
      destinationProjection: string,
      map: Map,
    ) => Promise<{
      label: string;
      value: number | null;
      unit: string;
      style: string;
      internalName: string;
      provider: string;
      dtnRows?: unknown[] }[]>,
}

export const generatePopupInfo = async ({
  layers,
  coordinate,
  popup,
  destinationProjection,
  map,
  pixel,
  getLayerTooltip,
} : GeneratePopupInfoOptions) => {
  let result = {};
  let mainIndex = 0;
  // eslint-disable-next-line no-restricted-syntax
  for await (const layer of layers) {
    if (layer) {
      if (layer.get('type') !== LayerName.PrealoadingGeoLayer) {
        if (LayerName.BaseMapLayer === layer.get('name')) {
          if (coordinate) {
            popup.setPosition(coordinate);
            result = { ...result, coordinates: transform(coordinate, get(destinationProjection) as Projection, 'EPSG:4326'), pixel };
          }
        } else {
          const entries = await getLayerTooltip(layer, coordinate, destinationProjection, map);
          // eslint-disable-next-line no-loop-func
          entries.forEach((entry) => {
            result = {
              ...result,
              [`${entry.label}&&${mainIndex}`]: entry,
            };
            mainIndex += 1;
          });
        }
      }
    }
  }
  return result;
};

export const resetPopup = (
  map : Map | null,
  setPopupInfo : Dispatch<SetStateAction<Record<string, unknown>>>,
) => {
  const popupOverlay = map?.getOverlayById('popup');
  if (popupOverlay) {
    map?.removeOverlay(popupOverlay);
    setPopupInfo({});
  }
};

export const getLayerByName = (
  map: Map | null,
  name : LayerName | string,
) : unknown => {
  const layer = map?.getLayers()
    .getArray()
    .find((layer) => layer.get('name') === name);
  return layer;
};

const normalizeRing = (ring: number[][]): number[][] => {
  const result = [ring[0].slice()];
  for (let i = 1; i < ring.length; i += 1) {
    const prevLon = result[i - 1][0];
    let lon = ring[i][0];
    while (lon - prevLon > 180) lon -= 360;
    while (prevLon - lon > 180) lon += 360;
    result.push([lon, ring[i][1]]);
  }
  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeAntimeridian = (geom: any): void => {
  const type = geom.getType();
  if (type === 'Polygon') {
    geom.setCoordinates(geom.getCoordinates().map(normalizeRing));
  } else if (type === 'MultiPolygon') {
    geom.setCoordinates(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      geom.getCoordinates().map((poly: any) => poly.map(normalizeRing)),
    );
  } else if (type === 'GeometryCollection') {
    geom.getGeometries().forEach(normalizeAntimeridian);
  }
};

export const createKMLLayer = (url: string, style?: Style): VectorLayer<VectorSource> => {
  const kmlFormat = new KML({ extractStyles: !style });
  const source = new VectorSource();
  fetch(url)
    .then((r) => r.text())
    .then((text) => {
      const features = kmlFormat.readFeatures(text, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:4326',
      });
      features.forEach((f) => {
        const geom = f.getGeometry();
        if (geom) {
          normalizeAntimeridian(geom);
          geom.transform('EPSG:4326', destinationProjection);
        }
      });
      source.addFeatures(features);
    });
  return new VectorLayer({ source, ...(style && { style }) });
};

export const createBaseLayer = (map: Map, baseLayer: BaseLayerConfig) => {
  const useOSM = BASE_LAYER_PROVIDER === 'osm' && baseLayer.layerName === LayerName.BaseMapLayer;
  const url = useOSM
    ? 'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    : `${staticLayersServiceApi}/${baseLayer.layerName}/{z}/{x}/{y}.png`;
  const XYZLayer = new TileLayer({
    source: new XYZ({
      url,
      tileSize: 256,
      ...(useOSM && {
        attributions: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }),
    }),
  });
  return XYZLayer;
};

export const getZoom = (zoom: number, layerName: string) => {
  const initialZoom = 2;
  if (zoom <= initialZoom + 1) {
    return '110m';
  }

  if (zoom > initialZoom + 1 && zoom <= initialZoom + 6) {
    return '50m';
  }
  if (
    layerName.includes('river')
    || layerName.includes('admin_0_countries')
  ) {
    return '50m';
  }

  return '10m';
};

// Longer than any request can last (connectionTimeout), so it never cuts a load in flight.
const OLD_SOURCE_FALLBACK_MS = Math.max(60000, 2 * connectionTimeout);

export const recreateSource = (
  {
    layer, params, currentProjection, name,
  } : RecreateSourceParams,
) => {
  const oldSource = layer.getSource() as ImageStatic;
  const oldParams = buildParamsFromLayerWithTime(oldSource);
  const source = createImageSource({ ...oldParams, ...params, currentProjection }) as ImageStatic;

  if (name) {
    source.set('name', name);
  }

  // The old source is disposed shortly after ITS OWN load ends (the preload counts those events,
  // so it cannot be disposed earlier). A source replaced before it ever loads never fires
  // imageloadend, so a long fallback releases it anyway. Disposing twice is harmless.
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
  const disposeOldSource = () => {
    if (fallbackTimer) clearTimeout(fallbackTimer);
    oldSource.dispose();
  };
  oldSource.once('imageloadend', () => { setTimeout(disposeOldSource, 5000); });
  fallbackTimer = setTimeout(disposeOldSource, OLD_SOURCE_FALLBACK_MS);
  return source;
};

export const transformARENAAlertsIntoLogString = (inputAlerts: string) => inputAlerts.replace(/,+$/, '');

export const extractARENAAlarmasFromRootState = (state: RootState) => {
  const ARENA_QUERY_KEY = `getIOPoint({"group":"${ARENALayerConfig.iopointData.group}","station":"${ARENALayerConfig.iopointData.station}","variable":"${ARENALayerConfig.iopointData.variable}"})`;
  return state['meconsole-api'].queries[ARENA_QUERY_KEY]?.data;
};
